import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Loader2, Plus, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  listSiteCheckpoints,
  saveSiteCheckpoint,
  setSiteCheckpointStatus,
  type SiteCheckpoint,
} from "@/lib/site-checkpoints.functions";

export const Route = createFileRoute("/_authenticated/sites/$siteId/checkpoints")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "On-site checkpoints — MissionReady" },
      {
        name: "description",
        content:
          "Configure standalone QR checkpoints guards can scan on site without starting a guard tour.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SiteCheckpointsPage,
});

type Draft = {
  id?: string;
  name: string;
  checkpointCode: string;
  requirement: "required" | "suggested";
  requiredOccurrences: number;
  scanIntervalMinutes: string;
  instructions: string;
  managerNote: string;
  status: "active" | "inactive";
};

const emptyDraft: Draft = {
  name: "",
  checkpointCode: "",
  requirement: "suggested",
  requiredOccurrences: 1,
  scanIntervalMinutes: "",
  instructions: "",
  managerNote: "",
  status: "active",
};

function SiteCheckpointsPage() {
  const { siteId } = useParams({ from: "/_authenticated/sites/$siteId/checkpoints" });
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const qc = useQueryClient();
  const fetchList = useServerFn(listSiteCheckpoints);
  const save = useServerFn(saveSiteCheckpoint);
  const setStatus = useServerFn(setSiteCheckpointStatus);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const listQ = useQuery({
    queryKey: ["site-checkpoints", orgId, siteId],
    queryFn: () => fetchList({ data: { orgId: orgId!, siteId } }),
    enabled: !!orgId,
  });

  const canManage = listQ.data?.canManage ?? false;

  async function submit() {
    if (!draft || !orgId) return;
    if (!draft.name.trim()) {
      toast.error("Give the checkpoint a name.");
      return;
    }
    setSaving(true);
    try {
      await save({
        data: {
          orgId,
          siteId,
          id: draft.id,
          name: draft.name.trim(),
          checkpointCode: draft.checkpointCode.trim() || null,
          requirement: draft.requirement,
          requiredOccurrences: draft.requiredOccurrences,
          scanIntervalMinutes: draft.scanIntervalMinutes
            ? Number(draft.scanIntervalMinutes)
            : null,
          instructions: draft.instructions.trim() || null,
          managerNote: draft.managerNote.trim() || null,
          referencePhotos: [],
          status: draft.status,
        },
      });
      toast.success(draft.id ? "Checkpoint updated." : "Checkpoint added.");
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ["site-checkpoints", orgId, siteId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't save the checkpoint.");
    } finally {
      setSaving(false);
    }
  }

  async function toggle(cp: SiteCheckpoint) {
    if (!orgId) return;
    try {
      await setStatus({
        data: { orgId, id: cp.id, status: cp.status === "active" ? "inactive" : "active" },
      });
      await qc.invalidateQueries({ queryKey: ["site-checkpoints", orgId, siteId] });
    } catch {
      toast.error("Couldn't update the checkpoint.");
    }
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center gap-2">
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          className="rounded-md p-1 hover:bg-background/10"
          aria-label="Back to site"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="font-[Bebas_Neue] text-2xl tracking-wide">ON-SITE CHECKPOINTS</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Guards can scan these QR checkpoints any time they are on site — no guard tour needs to be
        started or submitted. Guard tours and their checkpoints are unaffected.
      </p>

      {listQ.isLoading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading checkpoints…
        </div>
      ) : listQ.error ? (
        <p className="text-sm text-muted-foreground">
          We couldn't load checkpoints right now. Please try again.
        </p>
      ) : (
        <div className="space-y-2">
          {(listQ.data?.checkpoints ?? []).length === 0 && (
            <p className="rounded-lg border border-border/50 p-4 text-sm text-muted-foreground">
              No on-site checkpoints yet.
            </p>
          )}
          {(listQ.data?.checkpoints ?? []).map((cp) => (
            <div
              key={cp.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border/50 p-3"
            >
              <QrCode className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{cp.name}</div>
                <div className="text-xs text-muted-foreground">
                  {cp.requirement === "required" ? "Required" : "Suggested"} ·{" "}
                  {cp.required_occurrences}× per shift
                  {cp.scan_interval_minutes ? ` · every ${cp.scan_interval_minutes} min` : ""}
                  {cp.status !== "active" ? " · inactive" : ""}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setDraft({
                        id: cp.id,
                        name: cp.name,
                        checkpointCode: cp.checkpoint_code ?? "",
                        requirement: cp.requirement,
                        requiredOccurrences: cp.required_occurrences,
                        scanIntervalMinutes: cp.scan_interval_minutes
                          ? String(cp.scan_interval_minutes)
                          : "",
                        instructions: cp.instructions ?? "",
                        managerNote: cp.manager_note ?? "",
                        status: cp.status === "active" ? "active" : "inactive",
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void toggle(cp)}>
                    {cp.status === "active" ? "Deactivate" : "Activate"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && !draft && (
        <Button onClick={() => setDraft({ ...emptyDraft })}>
          <Plus className="mr-2 h-4 w-4" /> Add checkpoint
        </Button>
      )}

      {draft && (
        <div className="space-y-3 rounded-lg border border-border/50 p-4">
          <div>
            <Label>Name</Label>
            <Input aria-label="Name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="North gate"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Reference code (optional)</Label>
              <Input aria-label="Reference code (optional)"
                value={draft.checkpointCode}
                onChange={(e) => setDraft({ ...draft, checkpointCode: e.target.value })}
              />
            </div>
            <div>
              <Label>Requirement</Label>
              <select
                value={draft.requirement}
                onChange={(e) =>
                  setDraft({ ...draft, requirement: e.target.value as "required" | "suggested" })
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="suggested">Suggested</option>
                <option value="required">Required</option>
              </select>
            </div>
            <div>
              <Label>Scans expected per shift</Label>
              <Input aria-label="Scans expected per shift"
                type="number"
                min={1}
                max={50}
                value={draft.requiredOccurrences}
                onChange={(e) =>
                  setDraft({ ...draft, requiredOccurrences: Number(e.target.value) || 1 })
                }
              />
            </div>
            <div>
              <Label>Minimum minutes between scans (optional)</Label>
              <Input aria-label="Minimum minutes between scans (optional)"
                type="number"
                min={0}
                max={1440}
                value={draft.scanIntervalMinutes}
                onChange={(e) => setDraft({ ...draft, scanIntervalMinutes: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Guard instructions (optional)</Label>
            <Textarea aria-label="Guard instructions (optional)"
              value={draft.instructions}
              onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
              rows={2}
            />
          </div>
          <div>
            <Label>Note for guards on scan (optional)</Label>
            <Textarea aria-label="Note for guards on scan (optional)"
              value={draft.managerNote}
              onChange={(e) => setDraft({ ...draft, managerNote: e.target.value })}
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => void submit()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
